import axios, { AxiosInstance } from 'axios'
import Logger from '@web/common/Logger'
import { generateUid } from '@web/api/careChat/uidGenerator'
import { mapAmoCrmMessage, mapAmoCrmHistoryPayload } from '@web/api/careChat/messageMapper'
import {
  CareChatEvent,
  CARE_CHAT_EVENT_TYPE
} from '@web/types/careChatEvent'
import { CareChatOutgoingAttachment } from '@web/types/careChat'
import { withQueryParams } from '@web/common/url'

type EventListener = (event: CareChatEvent) => void

type JsonObject = Record<string, unknown>

interface PendingRequest {
  resolve: (value: JsonObject) => void
  reject: (reason: unknown) => void
  timer: ReturnType<typeof setTimeout> | null
}

export interface AmoCrmWsClientOptions {
  gsoBaseUrl: string
  buttonId: string
  buttonHash: string
  leadFieldId?: number | null
}

export interface AmoCrmConnectParams {
  userId: string
  userName: string
  phone?: string
  email?: string
}

const SETTINGS_PATH = '/social_button/settings/'
const HUMANS_PATH = '/humans'
const WS_VERSION_PARAM = 'v2=1'
const CLIENT_VERSION = '3'

const MAX_RECONNECT_DELAY_SECONDS = 8
const MAX_TOTAL_RECONNECT_ATTEMPTS = 24
const PENDING_REQUEST_TIMEOUT_MS = 15000
// amoCRM/Kommo в простое не шлёт ничего: нет pong, нет фоновых broadcast'ов.
// Поэтому «нет трафика» ≠ «сокет умер». Раньше порог тишины 60s давал
// ложноположительный TRIP каждые ~90s даже при здоровом коннекте — поднимаем
// до 3 минут, чтобы heartbeat ловил только реально зависшие сокеты, у которых
// onclose не пришёл.
const HEARTBEAT_INTERVAL_MS = 60000
const HEARTBEAT_GRACE_MS = 120000
const INIT_RESPONSE_TIMEOUT_MS = 15000
const WS_HANDSHAKE_TIMEOUT_MS = 10000
const ENSURE_CONNECTED_TIMEOUT_MS = 15000
const DISCONNECT_TIMESTAMP_BUFFER_SECONDS = 5

export class AmoCrmWsClient {
  private readonly axios: AxiosInstance
  private readonly options: AmoCrmWsClientOptions

  private listeners = new Set<EventListener>()
  private pendingRequests = new Map<string, PendingRequest>()

  private socket: WebSocket | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private initTimeoutTimer: ReturnType<typeof setTimeout> | null = null
  private initResolve: (() => void) | null = null
  private initReject: ((error: Error) => void) | null = null

  private socketUrl: string | null = null
  private chatId: string | null = null
  private conversationId: string | null = null
  private backendUrl: string | null = null
  private userId: string | null = null
  private customerUuid: string | null = null
  private siteId: string | null = null
  private fingerprint: string | null = null
  private userName: string | null = null
  private userPhone: string | undefined
  private userEmail: string | undefined
  private welcomeMessage: string | null = null
  private isMultiDialogsEnabled = false

  private connected = false
  private shouldReconnect = false
  private reconnecting = false
  private reconnectAttempt = 0
  private totalReconnectAttempt = 0
  private connectGeneration = 0
  private lastReceivedMessageTimestamp: number | null = null
  private disconnectedAtTimestamp: number | null = null
  private lastDataReceivedAt: number | null = null

  constructor (options: AmoCrmWsClientOptions) {
    this.options = options
    this.axios = axios.create({
      timeout: 10000
    })
  }

  // --- Public API ---

  get isConnected (): boolean {
    return this.connected
  }

  get currentChatId (): string | null {
    return this.chatId
  }

  get currentBackendUrl (): string | null {
    return this.backendUrl
  }

  addEventListener (listener: EventListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  async connect (params: AmoCrmConnectParams): Promise<void> {
    Logger.info('[CareChatWS] connect()')
    this.stopReconnect()
    this.closeChannel()

    this.userId = params.userId
    this.userName = params.userName
    this.userPhone = params.phone
    this.userEmail = params.email
    if (!this.fingerprint) this.fingerprint = generateUid()
    this.shouldReconnect = true
    this.lastReceivedMessageTimestamp = null
    this.disconnectedAtTimestamp = null
    this.chatId = null
    this.conversationId = null
    this.backendUrl = null
    this.customerUuid = null
    this.siteId = null
    this.isMultiDialogsEnabled = false
    const generation = ++this.connectGeneration

    try {
      await this.performFullHandshake(true)
    } catch (error) {
      if (generation !== this.connectGeneration) return
      Logger.error('[CareChatWS] connect error', error)
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.ERROR,
        errorMessage: this.formatError('Connection failed', error)
      })
      this.scheduleReconnect()
      throw error
    }
  }

  retryConnect (): void {
    Logger.info('[CareChatWS] retryConnect()')
    this.stopReconnect()
    this.closeChannel()
    this.totalReconnectAttempt = 0
    this.reconnectAttempt = 0
    this.shouldReconnect = true
    ++this.connectGeneration
    this.performFullHandshake(false).catch(error => {
      Logger.error('[CareChatWS] retryConnect failed', error)
    })
  }

  onAppResumed (): void {
    Logger.info('[CareChatWS] onAppResumed()')
    if (this.connected) {
      this.lastDataReceivedAt = Date.now()
      this.requestMissedMessages()
      return
    }
    if (!this.shouldReconnect) return
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnecting = false
    this.reconnectAttempt = 0
    this.totalReconnectAttempt = 0
    ++this.connectGeneration
    this.scheduleReconnect()
  }

  disconnect (): void {
    Logger.info('[CareChatWS] disconnect()')
    this.shouldReconnect = false
    this.stopReconnect()
    this.closeChannel()
    this.emit({ type: CARE_CHAT_EVENT_TYPE.DISCONNECTED })
  }

  dispose (): void {
    this.shouldReconnect = false
    this.stopReconnect()
    this.closeChannel()
    this.listeners.clear()
  }

  /**
   * Гарантирует живой коннект перед отправкой. Если сокет уже жив — resolve
   * сразу. Иначе форсирует реконнект (если ещё не запланирован) и ждёт
   * CONNECTED. Reject при CONNECTION_GAVE_UP или таймауте.
   */
  ensureConnectedOrReconnect (timeoutMs: number = ENSURE_CONNECTED_TIMEOUT_MS): Promise<void> {
    if (this.connected) return Promise.resolve()
    if (!this.shouldReconnect) {
      return Promise.reject(new Error('Client is disconnected; call connect() first'))
    }

    return new Promise<void>((resolve, reject) => {
      let settled = false
      const cleanup = () => {
        clearTimeout(timer)
        unsubscribe()
      }
      const unsubscribe = this.addEventListener(event => {
        if (settled) return
        if (event.type === CARE_CHAT_EVENT_TYPE.CONNECTED) {
          settled = true
          cleanup()
          resolve()
        } else if (event.type === CARE_CHAT_EVENT_TYPE.CONNECTION_GAVE_UP) {
          settled = true
          cleanup()
          reject(new Error('Reconnect gave up'))
        }
      })
      const timer = setTimeout(() => {
        if (settled) return
        settled = true
        cleanup()
        reject(new Error('Reconnect timeout'))
      }, timeoutMs)

      // Если реконнект ещё не запланирован/не в процессе — пнуть его.
      if (!this.reconnecting && !this.reconnectTimer) {
        this.scheduleReconnect()
      }
    })
  }

  sendMessage (text: string, uid: string = generateUid()): string | null {
    if (!this.ensureConnected()) return null
    this.sendWsMessageWithPending(uid, 'message', {
      ...this.buildChatParamMap(),
      message: { text, type: 'text' }
    })
    return uid
  }

  sendFileWithAttachment (attachment: CareChatOutgoingAttachment, uid: string = generateUid()): string | null {
    if (!this.ensureConnected()) return null
    this.sendWsMessageWithPending(uid, 'message', {
      ...this.buildChatParamMap(),
      message: {
        type: 'text',
        text: '',
        attachments: [attachment]
      }
    })
    return uid
  }

  async uploadAttachment (file: File): Promise<CareChatOutgoingAttachment | null> {
    const customerId = this.customerUuid ?? this.chatId
    if (!this.backendUrl || !customerId) {
      Logger.warn('[CareChatWS] uploadAttachment skipped: missing backendUrl or customerId')
      return null
    }

    try {
      const form = new FormData()
      form.append('attachment', file, file.name)

      const url = `${this.backendUrl}/attachment`
      const siteId = this.siteId ?? this.options.buttonId

      // Origin/Referer — «защищённые» заголовки: браузер запрещает их
      // выставлять из JS (выдаёт «Refused to set unsafe header»). Сервер
      // принимает их автоматически из контекста запроса, поэтому здесь не
      // указываем — иначе консоль засирается warning'ами на каждый аплоад.
      // Эти заголовки нужны только в Node/Dart-клиентах.
      const response = await this.axios.post<JsonObject>(url, form, {
        headers: {
          'X-Site-Id': siteId,
          'X-Customer-Id': customerId
        }
      })

      const data = response.data
      // Логируем сырой ответ всегда — у amoCRM/Kommo формат варьируется
      // (url может лежать на верхнем уровне, под link, во вложенном
      // attachment и т.д.). Это нужно для корректного извлечения URL в
      // useCareChat.sendFile.
      Logger.info('[CareChatWS] uploadAttachment response', {
        status: response.status,
        dataType: typeof data,
        data
      })
      // Любой объект-ответ считаем валидным attachment'ом — amoCRM умеет
      // потом сам подтянуть URL по идентификатору, главное скормить его в
      // WS-фрейм 'message' (так делает официальный виджет и flutter-клиент).
      // Раньше мы блокировали отправку, если на верхнем уровне нет `url`,
      // и теряли валидные ответы с URL во вложенных полях.
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        Logger.warn('[CareChatWS] uploadAttachment: response is not an object', data)
        return null
      }
      return data as CareChatOutgoingAttachment
    } catch (error) {
      Logger.error('[CareChatWS] uploadAttachment error', error)
      return null
    }
  }

  requestHistory (params: { lteTimestamp?: number; gteTimestamp?: number } = {}): void {
    if (!this.ensureConnected()) return
    const uid = generateUid()
    const historyParams: JsonObject = { ...this.buildChatParamMap() }
    if (params.lteTimestamp != null) historyParams.lte = params.lteTimestamp
    if (params.gteTimestamp != null) historyParams.gte = params.gteTimestamp
    this.sendWsMessageWithPending(uid, 'history', historyParams)
  }

  markAsRead (timestamp: number): void {
    if (!this.ensureConnected()) return
    const uid = generateUid()
    this.sendWsMessage(uid, 'read', {
      ...this.buildChatParamMap(),
      timestamp
    })
  }

  // --- Private: emit + helpers ---

  private emit (event: CareChatEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (error) {
        Logger.error('[CareChatWS] listener error', error)
      }
    }
  }

  private buildChatParamMap (): JsonObject {
    const params: JsonObject = { chat_id: this.chatId }
    if (this.isMultiDialogsEnabled && this.conversationId) {
      params.conversation_id = this.conversationId
    }
    return params
  }

  private ensureConnected (): boolean {
    if (!this.chatId || !this.socket || !this.connected) {
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.ERROR,
        errorMessage: 'Chat is not connected. Call connect() first.'
      })
      return false
    }
    return true
  }

  private formatError (prefix: string, error: unknown): string {
    if (error instanceof Error) return `${prefix}: ${error.message}`
    return `${prefix}: ${String(error)}`
  }

  // --- Private: handshake ---

  private async performFullHandshake (isInitialConnect: boolean): Promise<void> {
    const generation = this.connectGeneration
    if (!this.shouldReconnect) return

    const userId = this.userId
    const fingerprint = this.fingerprint
    if (!userId || !fingerprint) {
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.ERROR,
        errorMessage: 'userId or fingerprint is null. Call connect() first.'
      })
      return
    }

    try {
      const settings = await this.fetchSettings()
      if (generation !== this.connectGeneration || !this.shouldReconnect) return

      this.socketUrl = (settings.socket_url as string | undefined) ?? null
      this.siteId = settings.site_id != null ? String(settings.site_id) : null
      this.welcomeMessage = (settings.welcome_message as string | undefined) ?? null

      if (!this.socketUrl) throw new Error('Socket URL not found in settings')

      await this.registerVisitor(fingerprint, userId)
      if (generation !== this.connectGeneration || !this.shouldReconnect) return

      await this.connectWebSocket()
    } catch (error) {
      if (generation !== this.connectGeneration) return
      Logger.error('[CareChatWS] performFullHandshake error', error)
      if (isInitialConnect) throw error
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.ERROR,
        errorMessage: this.formatError('Reconnection failed', error)
      })
      this.scheduleReconnect()
    }
  }

  private async fetchSettings (): Promise<JsonObject> {
    const url = `${this.options.gsoBaseUrl}${SETTINGS_PATH}`
    const response = await this.axios.get<JsonObject>(url, {
      params: {
        id: this.options.buttonId,
        hash: this.options.buttonHash,
        client_version: CLIENT_VERSION
      }
    })

    const responseData = response.data
    if (!responseData || typeof responseData !== 'object') return {}

    const settingsMap = this.extractSettingsMap(responseData)
    if (!settingsMap) return {}

    const socialServices = settingsMap.social_services ?? settingsMap.social
    const serviceList = Array.isArray(socialServices)
      ? socialServices
      : (socialServices && typeof socialServices === 'object')
          ? Object.values(socialServices)
          : null

    if (serviceList) {
      for (const service of serviceList) {
        if (service && typeof service === 'object' && (service as JsonObject).type === 'livechat') {
          const svc = service as JsonObject
          const greeting = settingsMap.greeting
          const welcomeMessage = greeting && typeof greeting === 'object'
            ? (greeting as JsonObject).message
            : undefined
          return {
            socket_url: svc.link ?? svc.url,
            site_id: svc.id ?? this.options.buttonId,
            welcome_message: welcomeMessage
          }
        }
      }
    }

    return {}
  }

  private extractSettingsMap (responseData: JsonObject): JsonObject | null {
    const responseWrapper = responseData.response
    if (responseWrapper && typeof responseWrapper === 'object') {
      for (const accountData of Object.values(responseWrapper as JsonObject)) {
        if (accountData && typeof accountData === 'object') {
          const settings = (accountData as JsonObject).settings
          if (settings && typeof settings === 'object') {
            return settings as JsonObject
          }
        }
      }
    }
    if ('social_services' in responseData || 'social' in responseData) {
      return responseData
    }
    return null
  }

  private async registerVisitor (fingerprint: string, userId: string): Promise<void> {
    const url = `${this.options.gsoBaseUrl}${HUMANS_PATH}`
    await this.axios.get(url, {
      params: {
        visitor_uid: userId,
        id: this.options.buttonId,
        hash: this.options.buttonHash,
        fingerprint
      }
    })
  }

  // --- Private: WebSocket ---

  private async connectWebSocket (): Promise<void> {
    if (!this.socketUrl) throw new Error('socketUrl is null')

    const url = withQueryParams(this.socketUrl, WS_VERSION_PARAM)

    await new Promise<void>((resolve, reject) => {
      let settled = false
      const handshakeTimer = setTimeout(() => {
        if (settled) return
        settled = true
        try {
          socket.close()
        } catch (error) {
          Logger.warn('[CareChatWS] handshake close error', error)
        }
        reject(new Error('WebSocket handshake timeout'))
      }, WS_HANDSHAKE_TIMEOUT_MS)

      let socket: WebSocket
      try {
        socket = new WebSocket(url)
      } catch (error) {
        clearTimeout(handshakeTimer)
        reject(error)
        return
      }

      socket.onopen = () => {
        if (settled) return
        settled = true
        clearTimeout(handshakeTimer)
        this.socket = socket
        this.connected = true
        this.reconnectAttempt = 0
        this.totalReconnectAttempt = 0
        this.attachSocketListeners(socket)
        this.startHeartbeat()
        resolve()
      }

      socket.onerror = event => {
        if (settled) return
        settled = true
        clearTimeout(handshakeTimer)
        reject(new Error('WebSocket handshake error'))
        Logger.error('[CareChatWS] handshake error event', event)
      }
    })

    await this.sendInitAndWaitResponse()
  }

  private attachSocketListeners (socket: WebSocket): void {
    socket.onmessage = event => this.onWsMessage(event.data)
    socket.onerror = event => this.onWsError(event)
    socket.onclose = () => this.onWsDone()
  }

  private async sendInitAndWaitResponse (): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      this.initResolve = resolve
      this.initReject = reject

      this.initTimeoutTimer = setTimeout(() => {
        if (this.initResolve === resolve) {
          this.initResolve = null
          this.initReject = null
          reject(new Error('Init response timeout'))
        }
      }, INIT_RESPONSE_TIMEOUT_MS)

      this.sendInit()
    })

    if (this.initTimeoutTimer) {
      clearTimeout(this.initTimeoutTimer)
      this.initTimeoutTimer = null
    }
  }

  private sendInit (): void {
    const uid = generateUid()
    const params: JsonObject = {
      user: this.userId,
      name: this.userName
    }
    if (this.welcomeMessage) params.welcome_message = this.welcomeMessage
    if (this.userPhone) params.phone = this.userPhone
    if (this.userEmail) params.email = this.userEmail
    params.utm = `VisitorUid_${this.userId}`
    params.utm_source = 'lk_chat'
    params.id = this.options.leadFieldId
    params.bot_context = { step: 0 }
    const leadFieldId = this.options.leadFieldId
    if (leadFieldId) {
      params.lead = {
        custom_fields: [
          { id: leadFieldId, values: [{ value: 'lk_chat' }] }
        ]
      }
    }
    this.sendWsMessage(uid, 'init', params)
  }

  private sendWsMessage (uid: string, method: string, params: JsonObject): void {
    const message = { uid, method, params }
    try {
      this.socket?.send(JSON.stringify(message))
    } catch (error) {
      Logger.warn('[CareChatWS] sendWsMessage error', error)
    }
  }

  private sendWsMessageWithPending (uid: string, method: string, params: JsonObject): void {
    const timer = setTimeout(() => {
      const pending = this.pendingRequests.get(uid)
      if (!pending) return
      this.pendingRequests.delete(uid)
      pending.reject(new Error('Request timed out'))
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.REQUEST_FAILED,
        uid,
        errorMessage: 'Request timed out'
      })
    }, PENDING_REQUEST_TIMEOUT_MS)

    const pending: PendingRequest = {
      resolve: () => undefined,
      reject: () => undefined,
      timer
    }
    pending.resolve = value => {
      if (pending.timer) clearTimeout(pending.timer)
      Logger.info('[CareChatWS] pending response', { uid, value })
    }
    pending.reject = error => {
      if (pending.timer) clearTimeout(pending.timer)
      Logger.warn('[CareChatWS] pending rejected', { uid, error })
    }

    this.pendingRequests.set(uid, pending)
    this.sendWsMessage(uid, method, params)
  }

  private removePending (uid: string): PendingRequest | undefined {
    const pending = this.pendingRequests.get(uid)
    if (pending?.timer) clearTimeout(pending.timer)
    this.pendingRequests.delete(uid)
    return pending
  }

  // --- Private: WS message handling ---

  private onWsMessage (raw: unknown): void {
    this.onDataReceived()

    try {
      if (typeof raw !== 'string') return
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return
      const message = parsed as JsonObject

      const method = message.method != null ? String(message.method) : null
      switch (method) {
        case 'init':
          this.handleInitResponse(message)
          break
        case 'update':
        case 'open_conversation':
          this.handleUpdateResponse(message)
          break
        case 'typing':
          this.handleTypingResponse(message)
          break
        case 'close_conversation_req':
          this.emit({ type: CARE_CHAT_EVENT_TYPE.CONVERSATION_CLOSED })
          break
        default:
          this.handleGenericResponse(message)
      }
    } catch (error) {
      Logger.warn('[CareChatWS] onWsMessage parse error', error)
    }
  }

  private handleInitResponse (message: JsonObject): void {
    const payload = message.payload
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      this.completeInitWithError('Invalid init response: missing payload')
      return
    }
    const payloadMap = payload as JsonObject

    const chatData = payloadMap.chat
    const chatMap: JsonObject = chatData && typeof chatData === 'object' && !Array.isArray(chatData)
      ? chatData as JsonObject
      : {}

    this.chatId = chatMap.id != null ? String(chatMap.id) : null
    this.backendUrl = payloadMap.backend_url != null ? String(payloadMap.backend_url) : null
    this.customerUuid = payloadMap.uuid != null ? String(payloadMap.uuid) : null
    this.isMultiDialogsEnabled = payloadMap.multi_dialogs === true

    if (!this.chatId) {
      this.completeInitWithError('Invalid init response: missing chatId')
      return
    }

    const conversationIdList: string[] = []
    const conversations = chatMap.conversations
    if (Array.isArray(conversations)) {
      for (const conv of conversations) {
        if (conv && typeof conv === 'object' && (conv as JsonObject).id != null) {
          conversationIdList.push(String((conv as JsonObject).id))
        }
      }
    }

    if (conversationIdList.length > 0) {
      this.conversationId = conversationIdList[0]
    }

    if (this.initResolve) {
      const resolve = this.initResolve
      this.initResolve = null
      this.initReject = null
      resolve()
    }

    this.emit({
      type: CARE_CHAT_EVENT_TYPE.CONNECTED,
      chatId: this.chatId ?? undefined,
      conversationIdList,
      backendUrl: this.backendUrl ?? undefined
    })

    this.requestMissedMessages()
  }

  private completeInitWithError (errorMessage: string): void {
    if (this.initReject) {
      const reject = this.initReject
      this.initResolve = null
      this.initReject = null
      reject(new Error(errorMessage))
    }
    this.emit({ type: CARE_CHAT_EVENT_TYPE.ERROR, errorMessage })
  }

  private handleUpdateResponse (message: JsonObject): void {
    const payload = message.payload
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return
    const payloadMap = payload as JsonObject

    // amoCRM/Kommo шлёт payload.badge в самых разных служебных фреймах
    // (например, в read-эхо badge — это внутреннее поле события, а не
    // «у тебя N непрочитанных»). Принимаем серверный badge только если в
    // фрейме реально есть новое входящее сообщение — тогда оно достоверно
    // отражает unread по версии CRM. Чистые update-фреймы (read, typing
    // и пр.) счётчик не трогают, иначе он ложно растёт на 0 → N в простое.
    const updateList = Array.isArray(payloadMap.updates) ? payloadMap.updates : []
    const hasIncomingMessage = Boolean(payloadMap.message) ||
      (Array.isArray(payloadMap.messages) && payloadMap.messages.length > 0) ||
      updateList.some(u => !!(u && typeof u === 'object' && !Array.isArray(u) && (u as JsonObject).message))

    const serverBadgeCount = typeof payloadMap.badge === 'number' ? payloadMap.badge : undefined
    if (serverBadgeCount != null && hasIncomingMessage) {
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.BADGE_UPDATED,
        badgeCount: serverBadgeCount
      })
    }

    if (updateList.length > 0) {
      for (const update of updateList) {
        if (!update || typeof update !== 'object' || Array.isArray(update)) continue
        const updateMap = update as JsonObject

        const typingData = updateMap.typing
        if (typingData && typeof typingData === 'object' && !Array.isArray(typingData)) {
          const t = typingData as JsonObject
          this.emit({
            type: CARE_CHAT_EVENT_TYPE.MANAGER_TYPING,
            conversationId: t.chat_id != null ? String(t.chat_id) : undefined,
            expiredAtTimestamp: typeof t.expired_at === 'number' ? t.expired_at : undefined
          })
          continue
        }

        const messageData = updateMap.message
        if (messageData && typeof messageData === 'object' && !Array.isArray(messageData)) {
          const chatMessage = mapAmoCrmMessage(messageData as JsonObject)
          this.trackMessageTimestamp(chatMessage.timestamp)
          this.emit({
            type: CARE_CHAT_EVENT_TYPE.NEW_MESSAGE,
            message: chatMessage,
            serverBadgeCount
          })
        }
      }
      return
    }

    const messageData = payloadMap.message
    if (messageData && typeof messageData === 'object' && !Array.isArray(messageData)) {
      const chatMessage = mapAmoCrmMessage(messageData as JsonObject)
      this.trackMessageTimestamp(chatMessage.timestamp)
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.NEW_MESSAGE,
        message: chatMessage
      })
      return
    }

    const messageList = payloadMap.messages
    if (Array.isArray(messageList)) {
      for (const item of messageList) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const chatMessage = mapAmoCrmMessage(item as JsonObject)
          this.trackMessageTimestamp(chatMessage.timestamp)
          this.emit({
            type: CARE_CHAT_EVENT_TYPE.NEW_MESSAGE,
            message: chatMessage
          })
        }
      }
    }
  }

  private handleTypingResponse (message: JsonObject): void {
    const payload = message.payload
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const p = payload as JsonObject
      const conversationFromObject = p.conversation
      const conversationId = p.conversation_id != null
        ? String(p.conversation_id)
        : (conversationFromObject && typeof conversationFromObject === 'object'
            ? String((conversationFromObject as JsonObject).id ?? '') || undefined
            : undefined)
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.MANAGER_TYPING,
        conversationId,
        expiredAtTimestamp: typeof p.expired_at === 'number'
          ? p.expired_at
          : parseInt(String(p.expired_at ?? ''), 10) || undefined
      })
      return
    }

    const action = message.action
    if (action && typeof action === 'object' && !Array.isArray(action)) {
      const typing = (action as JsonObject).typing
      if (typing && typeof typing === 'object' && !Array.isArray(typing)) {
        const t = typing as JsonObject
        const conv = t.conversation
        this.emit({
          type: CARE_CHAT_EVENT_TYPE.MANAGER_TYPING,
          conversationId: conv && typeof conv === 'object' && !Array.isArray(conv)
            ? ((conv as JsonObject).id != null ? String((conv as JsonObject).id) : undefined)
            : undefined,
          expiredAtTimestamp: typeof t.expired_at === 'number'
            ? t.expired_at
            : parseInt(String(t.expired_at ?? ''), 10) || undefined
        })
      }
    }
  }

  private handleGenericResponse (message: JsonObject): void {
    const uid = message.uid != null ? String(message.uid) : null
    if (!uid) return

    const pending = this.removePending(uid)
    const payload = message.payload

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      pending?.resolve(message)
      return
    }
    const payloadMap = payload as JsonObject

    const messageData = payloadMap.message
    if (messageData && typeof messageData === 'object' && !Array.isArray(messageData)) {
      const chatMessage = mapAmoCrmMessage(messageData as JsonObject)
      this.trackMessageTimestamp(chatMessage.timestamp)
      pending?.resolve(message)
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.MESSAGE_SENT,
        localUid: uid,
        message: chatMessage
      })
      return
    }

    const messageList = payloadMap.messages
    if (Array.isArray(messageList)) {
      const messages = mapAmoCrmHistoryPayload(payloadMap)
      for (const m of messages) this.trackMessageTimestamp(m.timestamp)
      pending?.resolve(message)
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.HISTORY,
        messages
      })
      return
    }

    pending?.resolve(message)
  }

  // --- Private: timestamp tracking ---

  private trackMessageTimestamp (timestamp?: number): void {
    if (timestamp == null) return
    if (this.lastReceivedMessageTimestamp == null || timestamp > this.lastReceivedMessageTimestamp) {
      this.lastReceivedMessageTimestamp = timestamp
      this.disconnectedAtTimestamp = null
    }
  }

  private requestMissedMessages (): void {
    if (!this.chatId) return
    const from = this.lastReceivedMessageTimestamp ?? this.disconnectedAtTimestamp
    // Первый коннект (нет ни одного timestamp) — грузим полную историю.
    // Реконнект — только сообщения, пропущенные с момента обрыва.
    if (from == null) {
      this.requestHistory()
      return
    }
    this.requestHistory({ gteTimestamp: from })
  }

  // --- Private: heartbeat ---

  private startHeartbeat (): void {
    this.stopHeartbeat()
    this.lastDataReceivedAt = Date.now()
    this.heartbeatTimer = setInterval(() => this.checkConnectionAlive(), HEARTBEAT_INTERVAL_MS)
  }

  private stopHeartbeat (): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private onDataReceived (): void {
    this.lastDataReceivedAt = Date.now()
  }

  private checkConnectionAlive (): void {
    // Проверяем живость даже без pending-запросов: сокет может тихо
    // умереть в простое (юзер просто читает), и тогда входящие сообщения
    // менеджера были бы потеряны до первой неудачной отправки.
    if (!this.connected) return
    const lastData = this.lastDataReceivedAt
    if (lastData == null) return
    const silence = Date.now() - lastData
    if (silence > HEARTBEAT_INTERVAL_MS + HEARTBEAT_GRACE_MS) {
      Logger.warn('[CareChatWS] heartbeat TRIP — mark dead')
      this.connected = false
      if (this.shouldReconnect) this.scheduleReconnect()
    }
  }

  // --- Private: reconnect ---

  private onWsError (event: Event): void {
    Logger.warn('[CareChatWS] WebSocket error event', event)
    this.connected = false
    if (this.shouldReconnect) this.scheduleReconnect()
  }

  private onWsDone (): void {
    this.connected = false
    if (this.shouldReconnect) this.scheduleReconnect()
  }

  private scheduleReconnect (): void {
    if (!this.shouldReconnect || this.reconnecting) return

    if (this.disconnectedAtTimestamp == null) {
      this.disconnectedAtTimestamp = Math.floor(Date.now() / 1000) - DISCONNECT_TIMESTAMP_BUFFER_SECONDS
    }

    this.totalReconnectAttempt++
    if (this.totalReconnectAttempt > MAX_TOTAL_RECONNECT_ATTEMPTS) {
      this.shouldReconnect = false
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.CONNECTION_GAVE_UP,
        totalAttemptCount: this.totalReconnectAttempt
      })
      return
    }

    this.reconnecting = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.stopHeartbeat()

    if (this.initReject) {
      const reject = this.initReject
      this.initResolve = null
      this.initReject = null
      reject(new Error('Connection lost'))
    }

    this.closeSocketSilently()
    this.rejectAllPending()

    this.reconnectAttempt++
    const delaySeconds = this.reconnectAttempt <= 1
      ? 0
      : Math.min(1 << (this.reconnectAttempt - 2), MAX_RECONNECT_DELAY_SECONDS)

    this.emit({
      type: CARE_CHAT_EVENT_TYPE.RECONNECTING,
      attemptCount: this.totalReconnectAttempt
    })

    this.reconnectTimer = setTimeout(() => {
      this.reconnecting = false
      this.performFullHandshake(false).catch(error => {
        Logger.warn('[CareChatWS] scheduled handshake failed', error)
      })
    }, delaySeconds * 1000)
  }

  private rejectAllPending (): void {
    const uids = Array.from(this.pendingRequests.keys())
    for (const uid of uids) {
      const pending = this.removePending(uid)
      pending?.reject(new Error('WebSocket disconnected'))
      this.emit({
        type: CARE_CHAT_EVENT_TYPE.REQUEST_FAILED,
        uid,
        errorMessage: 'WebSocket disconnected'
      })
    }
  }

  private stopReconnect (): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnecting = false
    this.reconnectAttempt = 0
  }

  private closeChannel (): void {
    this.stopHeartbeat()
    if (this.initTimeoutTimer) {
      clearTimeout(this.initTimeoutTimer)
      this.initTimeoutTimer = null
    }
    if (this.initReject) {
      const reject = this.initReject
      this.initResolve = null
      this.initReject = null
      reject(new Error('Connection closed'))
    }
    this.closeSocketSilently()
    this.connected = false
    this.rejectAllPending()
  }

  private closeSocketSilently (): void {
    const socket = this.socket
    if (!socket) return
    try {
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close()
    } catch (error) {
      Logger.warn('[CareChatWS] socket close error', error)
    }
    this.socket = null
  }
}
